import React, { useEffect, useState } from 'react';
import { XIcon, VolumeIcon, MusicIcon, MonitorIcon, BellIcon, LockIcon } from 'lucide-react';
interface SettingsMenuProps {
  onClose: () => void;
}
export const SettingsMenu = ({
  onClose
}: SettingsMenuProps) => {
  const [activeTab, setActiveTab] = useState('audio');
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    // Initial check
    checkIfMobile();
    // Add event listener for window resize
    window.addEventListener('resize', checkIfMobile);
    // Cleanup
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);
  return <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center" data-id="element-599">
      <div className={`bg-[#282828] ${isMobile ? 'w-full h-full' : 'rounded-lg w-full max-w-4xl mx-4'} overflow-hidden flex flex-col md:flex-row`} data-id="element-600">
        {/* Header - Mobile only */}
        <div className="p-4 border-b border-white/10 flex items-center justify-between md:hidden" data-id="element-601">
          <h2 className="text-xl font-bold" data-id="element-602">Settings</h2>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors" data-id="element-603">
            <XIcon className="w-6 h-6" data-id="element-604" />
          </button>
        </div>
        {/* Sidebar */}
        <div className={`${isMobile ? 'border-b' : 'border-r'} border-white/10 md:w-64`} data-id="element-605">
          <div className="p-4 hidden md:flex items-center justify-between" data-id="element-606">
            <h2 className="text-xl font-bold" data-id="element-607">Settings</h2>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors" data-id="element-608">
              <XIcon className="w-6 h-6" data-id="element-609" />
            </button>
          </div>
          <nav className={`p-2 ${isMobile ? 'flex flex-wrap' : ''}`} data-id="element-610">
            <SettingsTab id="audio" label="Audio Quality" icon={<VolumeIcon data-id="element-612" />} active={activeTab === 'audio'} onClick={() => setActiveTab('audio')} isMobile={isMobile} data-id="element-611" />
            <SettingsTab id="playback" label="Playback" icon={<MusicIcon data-id="element-614" />} active={activeTab === 'playback'} onClick={() => setActiveTab('playback')} isMobile={isMobile} data-id="element-613" />
            <SettingsTab id="devices" label="Devices" icon={<MonitorIcon data-id="element-616" />} active={activeTab === 'devices'} onClick={() => setActiveTab('devices')} isMobile={isMobile} data-id="element-615" />
            <SettingsTab id="notifications" label="Notifications" icon={<BellIcon data-id="element-618" />} active={activeTab === 'notifications'} onClick={() => setActiveTab('notifications')} isMobile={isMobile} data-id="element-617" />
            <SettingsTab id="privacy" label="Privacy" icon={<LockIcon data-id="element-620" />} active={activeTab === 'privacy'} onClick={() => setActiveTab('privacy')} isMobile={isMobile} data-id="element-619" />
          </nav>
        </div>
        {/* Content */}
        <div className={`flex-1 p-6 ${isMobile ? 'max-h-[60vh]' : 'max-h-[70vh]'} overflow-y-auto`} data-id="element-621">
          {activeTab === 'audio' && <AudioSettings data-id="element-622" />}
          {activeTab === 'playback' && <PlaybackSettings data-id="element-623" />}
          {activeTab === 'devices' && <DevicesSettings data-id="element-624" />}
          {activeTab === 'notifications' && <NotificationsSettings data-id="element-625" />}
          {activeTab === 'privacy' && <PrivacySettings data-id="element-626" />}
        </div>
        {/* Mobile footer with close button */}
        {isMobile && <div className="p-4 border-t border-white/10 mt-auto" data-id="element-627">
            <button onClick={onClose} className="w-full bg-white text-black font-bold py-3 rounded-full" data-id="element-628">
              Close
            </button>
          </div>}
      </div>
    </div>;
};
interface SettingsTabProps {
  id: string;
  label: string;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
  isMobile?: boolean;
}
const SettingsTab = ({
  id,
  label,
  icon,
  active,
  onClick,
  isMobile = false
}: SettingsTabProps) => <button className={`
      flex items-center transition-colors
      ${active ? 'bg-white/10 text-white' : 'text-gray-400 hover:text-white'}
      ${isMobile ? 'px-3 py-2 m-1 rounded-full text-sm flex-shrink-0' : 'w-full px-3 py-2 rounded-md'}
    `} onClick={onClick} data-id="element-629">
    <span className={`${isMobile ? 'mr-1' : 'mr-3'}`} data-id="element-630">{icon}</span>
    <span data-id="element-631">{label}</span>
  </button>;
const AudioSettings = () => <div data-id="element-632">
    <h2 className="text-2xl font-bold mb-6" data-id="element-633">Audio Quality</h2>
    <div className="mb-8" data-id="element-634">
      <h3 className="text-lg font-semibold mb-3" data-id="element-635">Streaming quality</h3>
      <div className="space-y-2" data-id="element-636">
        <RadioOption name="streaming" value="auto" label="Automatic (Recommended)" defaultChecked data-id="element-637" />
        <RadioOption name="streaming" value="low" label="Low (24 kbit/s)" data-id="element-638" />
        <RadioOption name="streaming" value="normal" label="Normal (96 kbit/s)" data-id="element-639" />
        <RadioOption name="streaming" value="high" label="High (160 kbit/s)" data-id="element-640" />
        <RadioOption name="streaming" value="very-high" label="Very high (320 kbit/s)" data-id="element-641" />
      </div>
      <p className="text-sm text-gray-400 mt-2" data-id="element-642">
        Higher quality uses more data
      </p>
    </div>
    <div className="mb-8" data-id="element-643">
      <h3 className="text-lg font-semibold mb-3" data-id="element-644">Download quality</h3>
      <div className="space-y-2" data-id="element-645">
        <RadioOption name="download" value="normal" label="Normal (96 kbit/s)" data-id="element-646" />
        <RadioOption name="download" value="high" label="High (160 kbit/s)" data-id="element-647" />
        <RadioOption name="download" value="very-high" label="Very high (320 kbit/s)" defaultChecked data-id="element-648" />
      </div>
      <p className="text-sm text-gray-400 mt-2" data-id="element-649">
        Higher quality uses more storage
      </p>
    </div>
    <div data-id="element-650">
      <h3 className="text-lg font-semibold mb-3" data-id="element-651">Equalizer</h3>
      <button className="bg-white/10 hover:bg-white/20 transition-colors px-4 py-2 rounded-full text-sm" data-id="element-652">
        Open external equalizer
      </button>
    </div>
  </div>;
const PlaybackSettings = () => <div data-id="element-653">
    <h2 className="text-2xl font-bold mb-6" data-id="element-654">Playback</h2>
    <div className="mb-8" data-id="element-655">
      <h3 className="text-lg font-semibold mb-3" data-id="element-656">Crossfade</h3>
      <div className="flex items-center space-x-4" data-id="element-657">
        <span className="text-sm" data-id="element-658">Off</span>
        <input type="range" className="flex-1" min="0" max="12" step="1" defaultValue="0" data-id="element-659" />
        <span className="text-sm" data-id="element-660">12s</span>
      </div>
    </div>
    <div className="space-y-4" data-id="element-661">
      <ToggleOption label="Gapless playback" description="Allows songs to transition without a pause" defaultChecked={true} data-id="element-662" />
      <ToggleOption label="Automix" description="Allows smooth transitions between songs in a playlist" defaultChecked={true} data-id="element-663" />
      <ToggleOption label="Normalize volume" description="Set the same volume level for all tracks" defaultChecked={true} data-id="element-664" />
      <ToggleOption label="Autoplay" description="Keep playing similar songs when your music ends" defaultChecked={true} data-id="element-665" />
    </div>
  </div>;
const DevicesSettings = () => <div data-id="element-666">
    <h2 className="text-2xl font-bold mb-6" data-id="element-667">Devices</h2>
    <p className="text-gray-400 mb-4" data-id="element-668">No devices found</p>
    <button className="bg-white/10 hover:bg-white/20 transition-colors px-4 py-2 rounded-full text-sm" data-id="element-669">
      Find devices
    </button>
  </div>;
const NotificationsSettings = () => <div data-id="element-670">
    <h2 className="text-2xl font-bold mb-6" data-id="element-671">Notifications</h2>
    <div className="space-y-4" data-id="element-672">
      <ToggleOption label="New music notifications" description="Receive notifications when new music is released" defaultChecked={true} data-id="element-673" />
      <ToggleOption label="Playlist updates" description="Receive notifications when playlists you follow are updated" defaultChecked={true} data-id="element-674" />
      <ToggleOption label="Artist updates" description="Receive notifications about artists you follow" defaultChecked={false} data-id="element-675" />
      <ToggleOption label="Spotify announcements" description="Receive news and offers from Spotify" defaultChecked={false} data-id="element-676" />
    </div>
  </div>;
const PrivacySettings = () => <div data-id="element-677">
    <h2 className="text-2xl font-bold mb-6" data-id="element-678">Privacy</h2>
    <div className="space-y-4" data-id="element-679">
      <ToggleOption label="Private session" description="Listen without updating your Recently Played" defaultChecked={false} data-id="element-680" />
      <ToggleOption label="Show what I'm listening to" description="Let followers see what you're listening to" defaultChecked={true} data-id="element-681" />
      <ToggleOption label="Personalized ads" description="Allow Spotify to use your data for personalized ads" defaultChecked={true} data-id="element-682" />
    </div>
    <button className="mt-6 bg-white/10 hover:bg-white/20 transition-colors px-4 py-2 rounded-full text-sm" data-id="element-683">
      Clear recent searches
    </button>
  </div>;
interface RadioOptionProps {
  name: string;
  value: string;
  label: string;
  defaultChecked?: boolean;
}
const RadioOption = ({
  name,
  value,
  label,
  defaultChecked
}: RadioOptionProps) => <label className="flex items-center" data-id="element-684">
    <input type="radio" name={name} value={value} defaultChecked={defaultChecked} className="mr-2" data-id="element-685" />
    <span data-id="element-686">{label}</span>
  </label>;
interface ToggleOptionProps {
  label: string;
  description: string;
  defaultChecked: boolean;
}
const ToggleOption = ({
  label,
  description,
  defaultChecked
}: ToggleOptionProps) => <div className="flex items-center justify-between" data-id="element-687">
    <div data-id="element-688">
      <div className="font-medium" data-id="element-689">{label}</div>
      <div className="text-sm text-gray-400" data-id="element-690">{description}</div>
    </div>
    <label className="relative inline-block w-12 h-6" data-id="element-691">
      <input type="checkbox" className="opacity-0 w-0 h-0" defaultChecked={defaultChecked} data-id="element-692" />
      <span className={`absolute cursor-pointer inset-0 rounded-full ${defaultChecked ? 'bg-green-500' : 'bg-gray-600'}`} data-id="element-693">
        <span className={`absolute w-4 h-4 bg-white rounded-full top-1 transition-all ${defaultChecked ? 'left-7' : 'left-1'}`} data-id="element-694"></span>
      </span>
    </label>
  </div>;